import React, { useEffect, useRef, useState } from 'react'
import { getAiCodeFeatures } from '../../../hooks/useBrandConfig'
import {
    loadOutputs,
    saveOutputs,
    addOutput,
    OUTPUT_TYPES,
} from '../outputInstances'
import { effectiveAspect } from '../vision/cameraConfig'
import styles from './AiCodeConfigure.module.css'
import { X, Eye, EyeOff, Volume2 } from 'lucide-react'

// Output layout editor. Places the camera plus any number of output instances
// (Text/Image/Video/Button) on the screen; each can be dragged/resized/removed.
// Camera position -> aiCodeLayout.camera; instances -> aiCodeOutputs.
const LAYOUT_KEY = 'aiCodeLayout'
const DEFAULT_CAMERA = { x: 8, y: 8, w: 84, h: 55 }
// On-screen Button / Toggle labels are kept short so they fit the widget.
const NAME_MAX_LEN = 16

const loadSelectedFeatures = () => {
    try {
        return JSON.parse(sessionStorage.getItem('aiCodeSelectedFeatures')) || []
    } catch {
        return []
    }
}

const clamp = (v, min, max) => Math.max(min, Math.min(max, v))

const LayoutEditor = () => {
    const frameRef = useRef(null)
    const drag = useRef(null)

    const enable = (() => {
        try {
            return JSON.parse(sessionStorage.getItem('enable')) || {}
        } catch {
            return {}
        }
    })()
    const hasCam = enable?.s1?.cam === true
    const selectedFeatures = loadSelectedFeatures()
    const enabledOutputs = getAiCodeFeatures('outputs').filter(
        (f) => f.enabled !== false
    )
    const enabledInputs = getAiCodeFeatures('inputs').filter(
        (f) => f.enabled !== false
    )
    // Screen-placeable widgets = the display outputs picked on Select Feature,
    // PLUS the on-screen input controls (Button / Toggle) — these moved to the
    // Inputs category but are still placed on the play screen here.
    const SCREEN_INPUT_SLUGS = ['button', 'toggle']
    const addable = [
        ...enabledOutputs.filter((f) =>
            selectedFeatures.includes(`outputs/${f.slug}`)
        ),
        ...enabledInputs.filter(
            (f) =>
                SCREEN_INPUT_SLUGS.includes(f.slug) &&
                selectedFeatures.includes(`inputs/${f.slug}`)
        ),
    ]
        .filter((f) => OUTPUT_TYPES[f.slug])
        .map((f) => f.slug)

    const [camera, setCamera] = useState(() => {
        try {
            const l = JSON.parse(sessionStorage.getItem(LAYOUT_KEY)) || {}
            return l.camera || DEFAULT_CAMERA
        } catch {
            return DEFAULT_CAMERA
        }
    })
    // The camera is shown by default but can be removed; only one is allowed.
    const [cameraRemoved, setCameraRemoved] = useState(() => {
        try {
            const l = JSON.parse(sessionStorage.getItem(LAYOUT_KEY)) || {}
            return l.cameraRemoved === true
        } catch {
            return false
        }
    })
    const [outputs, setOutputs] = useState(loadOutputs)

    const persistCamera = (pos) => {
        setCamera(pos)
        const l = JSON.parse(sessionStorage.getItem(LAYOUT_KEY) || '{}')
        l.camera = pos
        sessionStorage.setItem(LAYOUT_KEY, JSON.stringify(l))
    }
    const persistCameraRemoved = (removed) => {
        setCameraRemoved(removed)
        const l = JSON.parse(sessionStorage.getItem(LAYOUT_KEY) || '{}')
        l.cameraRemoved = removed
        sessionStorage.setItem(LAYOUT_KEY, JSON.stringify(l))
    }
    const persistOutputs = (list) => {
        setOutputs(list)
        saveOutputs(list)
    }

    // The camera box adopts the aspect ratio chosen in Inputs. Pixel aspect =
    // (w%·frameW)/(h%·frameH); the phone frame is window-aspect, so for a target
    // camera ratio: h% = w% · windowAspect / cameraRatio.
    const windowAspect =
        typeof window !== 'undefined' && window.innerHeight
            ? window.innerWidth / window.innerHeight
            : 16 / 9
    const camAspect = effectiveAspect()
    const camHeightFor = (w) => clamp((w * windowAspect) / camAspect, 6, 100)
    // Normalise the stored camera height to the chosen ratio on mount.
    useEffect(() => {
        const h = camHeightFor(camera.w)
        if (Math.abs(h - camera.h) > 0.5) persistCamera({ ...camera, h })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const onAdd = (type) => persistOutputs(addOutput(outputs, type))
    const onRemove = (id) => persistOutputs(outputs.filter((o) => o.id !== id))
    // Rename an output instance; the name shows on the Execute screen and in the
    // decision panel's Button / Toggle picker.
    const onRename = (id, name) =>
        persistOutputs(outputs.map((o) => (o.id === id ? { ...o, name } : o)))
    // Audio can be hidden (invisible) on the play screen while still playing.
    const onToggleHidden = (id) =>
        persistOutputs(
            outputs.map((o) => (o.id === id ? { ...o, hidden: !o.hidden } : o))
        )

    const onPointerMove = (e) => {
        const d = drag.current
        if (!d) return
        const dxPct = ((e.clientX - d.startX) / d.rect.width) * 100
        const dyPct = ((e.clientY - d.startY) / d.rect.height) * 100
        const cur = { ...d.orig }
        if (d.mode === 'move') {
            cur.x = clamp(d.orig.x + dxPct, 0, 100 - d.orig.w)
            cur.y = clamp(d.orig.y + dyPct, 0, 100 - d.orig.h)
        } else if (d.id === 'camera') {
            // The camera keeps the chosen aspect ratio: resize the width, derive
            // the height, so its shape is fixed and the drawn line stays put.
            const w = clamp(d.orig.w + dxPct, 8, 100 - d.orig.x)
            cur.w = w
            cur.h = camHeightFor(w)
        } else {
            cur.w = clamp(d.orig.w + dxPct, 8, 100 - d.orig.x)
            cur.h = clamp(d.orig.h + dyPct, 6, 100 - d.orig.y)
        }
        d.latest = cur
        if (d.id === 'camera') setCamera(cur)
        else
            setOutputs((prev) =>
                prev.map((o) => (o.id === d.id ? { ...o, ...cur } : o))
            )
    }
    const onPointerUp = () => {
        window.removeEventListener('pointermove', onPointerMove)
        window.removeEventListener('pointerup', onPointerUp)
        const d = drag.current
        if (d && d.latest) {
            if (d.id === 'camera') persistCamera(d.latest)
            else
                persistOutputs(
                    outputs.map((o) =>
                        o.id === d.id ? { ...o, ...d.latest } : o
                    )
                )
        }
        drag.current = null
    }
    const onPointerDown = (e, item, mode) => {
        e.preventDefault()
        e.stopPropagation()
        const rect = frameRef.current.getBoundingClientRect()
        drag.current = {
            id: item.id,
            mode,
            startX: e.clientX,
            startY: e.clientY,
            rect,
            orig: { x: item.x, y: item.y, w: item.w, h: item.h },
            latest: null,
        }
        window.addEventListener('pointermove', onPointerMove)
        window.addEventListener('pointerup', onPointerUp)
    }

    const screenRatio =
        typeof window !== 'undefined' && window.innerHeight
            ? window.innerWidth / window.innerHeight
            : 16 / 9

    const showCamera = hasCam && !cameraRemoved
    const widgets = [
        ...(showCamera
            ? [{ id: 'camera', type: 'camera', name: 'Camera', ...camera }]
            : []),
        ...outputs,
    ]

    return (
        <div className={styles.layoutWrap}>
            <div className={styles.outputToolbar}>
                <span className={styles.layoutHint}>Add to screen:</span>
                {hasCam && cameraRemoved && (
                    <button
                        className={styles.addOutputBtn}
                        onClick={() => persistCameraRemoved(false)}
                    >
                        + Camera
                    </button>
                )}
                {addable.length === 0 && !(hasCam && cameraRemoved) && (
                    <span className={styles.layoutHint}>
                        Pick outputs, buttons or toggles on Select Feature first.
                    </span>
                )}
                {addable.map((type) => (
                    <button
                        key={type}
                        className={styles.addOutputBtn}
                        onClick={() => onAdd(type)}
                    >
                        + {OUTPUT_TYPES[type].label}
                    </button>
                ))}
            </div>
            <p className={styles.layoutHint}>
                Drag to move, drag the corner to resize, × to remove.
            </p>
            <div
                className={styles.phoneFrame}
                ref={frameRef}
                style={{ aspectRatio: `${screenRatio}` }}
            >
                {widgets.length === 0 && (
                    <p className={styles.layoutEmpty}>
                        Enable a camera or add an output to place it here.
                    </p>
                )}
                {widgets.map((w) => (
                    <div
                        key={w.id}
                        className={`${styles.widget} ${styles['widget_' + w.type] || ''
                            }`}
                        style={{
                            left: `${w.x}%`,
                            top: `${w.y}%`,
                            width: `${w.w}%`,
                            height: `${w.h}%`,
                        }}
                        onPointerDown={(e) => onPointerDown(e, w, 'move')}
                    >
                        {w.type === 'button' || w.type === 'toggle' ? (
                            <input
                                className={styles.widgetNameInput}
                                value={w.name}
                                maxLength={NAME_MAX_LEN}
                                onChange={(e) =>
                                    onRename(
                                        w.id,
                                        e.target.value.slice(0, NAME_MAX_LEN)
                                    )
                                }
                                onPointerDown={(e) => e.stopPropagation()}
                                title="Rename (max 16 characters)"
                            />
                        ) : w.type === 'audio' ? (
                            <div className={styles.audioWidgetBody}>
                                <Volume2 size={18} />
                                <span className={styles.widgetLabel}>
                                    {w.name}
                                </span>
                                <button
                                    className={styles.audioVisBtn}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        onToggleHidden(w.id)
                                    }}
                                    title={
                                        w.hidden
                                            ? 'Hidden on screen (tap to show)'
                                            : 'Visible on screen (tap to hide)'
                                    }
                                >
                                    {w.hidden ? (
                                        <EyeOff size={14} />
                                    ) : (
                                        <Eye size={14} />
                                    )}
                                </button>
                            </div>
                        ) : (
                            <span className={styles.widgetLabel}>{w.name}</span>
                        )}
                        <button
                            className={styles.widgetRemove}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                                e.stopPropagation()
                                if (w.id === 'camera')
                                    persistCameraRemoved(true)
                                else onRemove(w.id)
                            }}
                        >
                            <X size={16} strokeWidth={2} />
                        </button>
                        <div
                            className={styles.resizeHandle}
                            onPointerDown={(e) => onPointerDown(e, w, 'resize')}
                        />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default LayoutEditor
